import formatsPlugin from "ajv-formats";
import {
  Ajv2020,
  type AnySchemaObject,
  type ErrorObject,
  type KeywordDefinition,
  type ValidateFunction,
} from "ajv/dist/2020.js";
import type { FormatsPlugin } from "ajv-formats";
import { z } from "zod";
import type {
  BrowserUseOutputForSchema,
  SkillArtifact,
  SkillId,
  SkillInput,
  SkillOutput,
  SkillPause,
  SkillRequestMetadata,
  SkillResume,
  SkillSignal,
} from "./contracts.js";
import { contractSchema } from "./generated/schemas.js";
import {
  CONTRACT_VERSION,
  contractRevision,
  contractSchemaId,
} from "./generated/metadata.js";
import {
  commonSchemaNames,
  skillIds,
  skillSchemaNames,
  type ContractSchemaDefinitions,
  type ContractSchemaName,
} from "./generated/skill-definitions.js";

const addFormats = formatsPlugin as unknown as FormatsPlugin;

function isSchemaObject(value: unknown): value is AnySchemaObject {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function uniquePropertiesKeyword(): KeywordDefinition {
  return {
    keyword: "x-uniqueProperties",
    type: "array",
    schemaType: "array",
    metaSchema: {
      type: "array",
      items: { type: "string", minLength: 1 },
      minItems: 1,
      uniqueItems: true,
    },
    compile(schema: unknown) {
      if (
        !Array.isArray(schema) ||
        schema.length === 0 ||
        !schema.every(
          (property): property is string =>
            typeof property === "string" && property.length > 0,
        )
      ) {
        throw new TypeError(
          "x-uniqueProperties requires a non-empty array of property names",
        );
      }

      return (data: unknown): boolean => {
        if (!Array.isArray(data)) return true;

        for (const property of schema) {
          const seen = new Set<unknown>();
          for (const item of data) {
            if (
              !isSchemaObject(item) ||
              !Object.prototype.hasOwnProperty.call(item, property)
            ) {
              continue;
            }
            const value = item[property];
            if (seen.has(value)) return false;
            seen.add(value);
          }
        }
        return true;
      };
    },
    errors: false,
  };
}

function createAjv(schema: unknown, strict = true): Ajv2020 {
  if (!isSchemaObject(schema)) {
    throw new TypeError("The generated A2A contract must be a schema object");
  }
  const instance = new Ajv2020({
    allErrors: true,
    coerceTypes: false,
    removeAdditional: false,
    useDefaults: false,
    strict,
    validateFormats: true,
  });
  addFormats(instance);
  // Pydantic emits OpenAPI's `discriminator` annotation alongside complete
  // JSON Schema `oneOf` + `const` constraints. Ajv does not support Pydantic's
  // mapping form natively, so register it as an annotation; `oneOf` remains the
  // authoritative validation rule.
  instance.addKeyword({
    keyword: "discriminator",
    schemaType: "object",
    valid: true,
  });
  // Standard JSON Schema cannot express uniqueness projected through one or
  // more object properties. The canonical Pydantic schema publishes this
  // extension where needed, and every generated runtime enforces it here.
  instance.addKeyword(uniquePropertiesKeyword());
  instance.addSchema(schema, contractSchemaId);
  return instance;
}

/**
 * Derive the consumer view from the canonical schema without changing any
 * known field constraint. Only closed-object markers are removed, allowing a
 * newer v1 producer to add fields that an older consumer does not know yet.
 */
function additiveCompatibleSchema(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(additiveCompatibleSchema);
  if (value === null || typeof value !== "object") return value;

  const compatible: Record<string, unknown> = {};
  for (const [key, child] of Object.entries(value)) {
    if (
      (key === "additionalProperties" || key === "unevaluatedProperties") &&
      child === false
    ) {
      continue;
    }
    compatible[key] = additiveCompatibleSchema(child);
  }
  return compatible;
}

/**
 * Memoize a factory. Ajv compilation is deferred to first validation so that
 * importing this module (pulled into every frontend page through
 * `@erp/shared-types`) never compiles a schema.
 */
function once<T>(factory: () => T): () => T {
  let value: { current: T } | null = null;
  return () => {
    value ??= { current: factory() };
    return value.current;
  };
}

const strictAjv = once(() => createAjv(contractSchema));
const compatibleContractSchema = once(() =>
  additiveCompatibleSchema(contractSchema),
);
const compatibleAjv = once(() => createAjv(compatibleContractSchema()));
// Branch selectors compile references to schema fragments. Some generated
// `allOf` fragments inherit their object type from a parent and are therefore
// intentionally incomplete when compiled alone. The complete compatible root
// has already validated the value; this non-strict instance only identifies
// which validated union branch should guide projection.
const compatibleProjectionAjv = once(() =>
  createAjv(compatibleContractSchema(), false),
);

function escapeJsonPointerToken(value: string): string {
  return value.replace(/~/g, "~0").replace(/\//g, "~1");
}

function unescapeJsonPointerToken(value: string): string {
  return value.replace(/~1/g, "/").replace(/~0/g, "~");
}

function schemaReference(name: ContractSchemaName): string {
  return `${contractSchemaId}#/$defs/${escapeJsonPointerToken(name)}`;
}

function schemaPointer(name: ContractSchemaName): string {
  return `#/$defs/${escapeJsonPointerToken(name)}`;
}

function schemaAtPointer(pointer: string): unknown {
  if (pointer === "#") return contractSchema;
  if (!pointer.startsWith("#/")) {
    throw new TypeError(
      `Unsupported non-local A2A schema reference: ${pointer}`,
    );
  }

  let value: unknown = contractSchema;
  for (const token of pointer
    .slice(2)
    .split("/")
    .map(unescapeJsonPointerToken)) {
    if (!isSchemaObject(value) || !(token in value)) {
      throw new TypeError(`Unknown A2A schema reference: ${pointer}`);
    }
    value = value[token];
  }
  return value;
}

function childSchemaPointer(
  pointer: string,
  property: string,
  index?: number | string,
): string {
  const suffix =
    index === undefined
      ? `/${escapeJsonPointerToken(property)}`
      : `/${escapeJsonPointerToken(property)}/${escapeJsonPointerToken(String(index))}`;
  return `${pointer}${suffix}`;
}

function cloneJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(cloneJsonValue);
  if (value === null || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [key, cloneJsonValue(child)]),
  );
}

const compatiblePointerValidators = new Map<string, ValidateFunction>();

function compatibleValueMatches(pointer: string, value: unknown): boolean {
  let validate = compatiblePointerValidators.get(pointer);
  if (!validate) {
    validate = compatibleProjectionAjv().compile({
      $ref: `${contractSchemaId}${pointer}`,
    });
    compatiblePointerValidators.set(pointer, validate);
  }
  return validate(value);
}

/**
 * Clone one compatible inbound value while retaining only fields published by
 * canonical closed objects. Deliberately open maps (for example JSON output,
 * action args, and eval state) retain every key, with their values cloned.
 *
 * Validation happens before this projection. Union branches are therefore
 * selected with the already-open compatible schema, then projected through the
 * matching canonical branch. This avoids mutating caller-owned protocol values
 * and prevents additive fields from leaking into typed application callbacks.
 */
function projectCompatibleValue(
  value: unknown,
  schema: unknown,
  pointer: string,
): unknown {
  if (typeof schema === "boolean") {
    return cloneJsonValue(value);
  }
  if (!isSchemaObject(schema)) {
    return cloneJsonValue(value);
  }

  const reference = schema.$ref;
  if (typeof reference === "string") {
    const localPointer = reference.startsWith(contractSchemaId)
      ? reference.slice(contractSchemaId.length)
      : reference;
    return projectCompatibleValue(
      value,
      schemaAtPointer(localPointer),
      localPointer,
    );
  }

  let projected = cloneJsonValue(value);
  for (const keyword of ["oneOf", "anyOf"] as const) {
    const branches = schema[keyword];
    if (!Array.isArray(branches)) continue;
    const branchIndex = branches.findIndex((_branch, index) =>
      compatibleValueMatches(
        childSchemaPointer(pointer, keyword, index),
        value,
      ),
    );
    if (branchIndex >= 0) {
      const branchPointer = childSchemaPointer(pointer, keyword, branchIndex);
      projected = projectCompatibleValue(
        projected,
        branches[branchIndex],
        branchPointer,
      );
    }
  }

  const allOf = schema.allOf;
  if (Array.isArray(allOf)) {
    for (const [index, branch] of allOf.entries()) {
      projected = projectCompatibleValue(
        projected,
        branch,
        childSchemaPointer(pointer, "allOf", index),
      );
    }
  }

  if (Array.isArray(projected)) {
    const items = schema.items;
    if (items === undefined) return projected;
    return projected.map((item) =>
      projectCompatibleValue(item, items, childSchemaPointer(pointer, "items")),
    );
  }
  if (projected === null || typeof projected !== "object") return projected;

  const properties = isSchemaObject(schema.properties) ? schema.properties : {};
  const closed =
    schema.additionalProperties === false ||
    schema.unevaluatedProperties === false;
  const additional = schema.additionalProperties;
  const output: Record<string, unknown> = {};

  for (const [key, child] of Object.entries(projected)) {
    const propertySchema = properties[key];
    if (propertySchema !== undefined) {
      output[key] = projectCompatibleValue(
        child,
        propertySchema,
        childSchemaPointer(childSchemaPointer(pointer, "properties"), key),
      );
      continue;
    }
    if (closed) continue;
    output[key] =
      additional !== undefined && additional !== true && additional !== false
        ? projectCompatibleValue(
            child,
            additional,
            childSchemaPointer(pointer, "additionalProperties"),
          )
        : cloneJsonValue(child);
  }
  return output;
}

function compileValidator<Name extends ContractSchemaName>(
  validator: Ajv2020,
  schemaName: Name,
): ValidateFunction<ContractSchemaDefinitions[Name]> {
  return validator.compile<ContractSchemaDefinitions[Name]>({
    $ref: schemaReference(schemaName),
  });
}

function errorPath(error: ErrorObject): PropertyKey[] {
  const path = error.instancePath
    ? error.instancePath
        .slice(1)
        .split("/")
        .map((token) => token.replace(/~1/g, "/").replace(/~0/g, "~"))
    : [];
  const child =
    error.keyword === "required"
      ? error.params.missingProperty
      : error.keyword === "additionalProperties"
        ? error.params.additionalProperty
        : null;
  return typeof child === "string" ? [...path, child] : path;
}

function errorMessage(error: ErrorObject): string {
  const message = error.message ?? "does not match the contract";
  return `${error.keyword}: ${message}`;
}

/**
 * Wrap an Ajv validator as a Zod schema without translating or duplicating the
 * canonical JSON Schema. The returned schema reports all Ajv issues with their
 * original object paths.
 */
export function contractZodSchema<Name extends ContractSchemaName>(
  schemaName: Name,
): z.ZodType<ContractSchemaDefinitions[Name]> {
  return zodSchemaForValidator(strictAjv, schemaName);
}

/**
 * Consumer validator for additive v1 evolution. It permits unknown object
 * fields but applies every canonical constraint to fields and variants it
 * recognizes. Unknown discriminated variants remain invalid so the stream
 * adapter can meter and ignore them explicitly.
 */
export function compatibleContractZodSchema<Name extends ContractSchemaName>(
  schemaName: Name,
): z.ZodType<ContractSchemaDefinitions[Name]> {
  return zodSchemaForValidator(compatibleAjv, schemaName, true);
}

function zodSchemaForValidator<Name extends ContractSchemaName>(
  validator: () => Ajv2020,
  schemaName: Name,
  projectCompatible = false,
): z.ZodType<ContractSchemaDefinitions[Name]> {
  const compiled = once(() => compileValidator(validator(), schemaName));
  const schema = z
    .unknown()
    .superRefine((value, context) => {
      const validate = compiled();
      if (validate(value)) return;

      for (const error of validate.errors ?? []) {
        context.addIssue({
          code: "custom",
          message: errorMessage(error),
          path: errorPath(error),
        });
      }
    })
    .transform((value) =>
      projectCompatible
        ? projectCompatibleValue(
            value,
            schemaAtPointer(schemaPointer(schemaName)),
            schemaPointer(schemaName),
          )
        : value,
    );
  // Ajv's ValidateFunction is the type guard for this exact generated root.
  // Zod cannot infer the validator + projection output, so restore its proven
  // generated type.
  return schema as z.ZodType<ContractSchemaDefinitions[Name]>;
}

/** Strict routing-metadata validator generated from `ContractMetadata`. */
export const contractMetadataSchema = contractZodSchema("ContractMetadata");

/** Additive-field-compatible routing-metadata validator for inbound messages. */
export const compatibleContractMetadataSchema =
  compatibleContractZodSchema("ContractMetadata");

/** Strict validator for the canonical operator action-step DTO. */
export const operatorStepSchema = contractZodSchema("OperatorStep");

/** Additive-field-compatible operator-step validator for inbound messages. */
export const compatibleOperatorStepSchema =
  compatibleContractZodSchema("OperatorStep");

/** Strict structured failure validator. Unknown error codes are rejected. */
export const runErrorSchema = contractZodSchema("RunError");

/** Additive-field-compatible structured failure validator. */
export const compatibleRunErrorSchema = compatibleContractZodSchema("RunError");

/** Strict validator for one authored SOP branch and its alternate paths. */
export const sopBranchSchema = contractZodSchema(commonSchemaNames.sopBranch);

/** Additive-field-compatible authored SOP branch validator. */
export const compatibleSopBranchSchema = compatibleContractZodSchema(
  commonSchemaNames.sopBranch,
);

/** Strict validator for one declared SOP runtime parameter. */
export const sopParamSchema = contractZodSchema(commonSchemaNames.sopParam);

/** Additive-field-compatible SOP runtime-parameter validator. */
export const compatibleSopParamSchema = compatibleContractZodSchema(
  commonSchemaNames.sopParam,
);

/** Strict validator for one canonical structured SOP step. */
export const sopStepSchema = contractZodSchema(commonSchemaNames.sopStep);

/** Additive-field-compatible canonical SOP-step validator. */
export const compatibleSopStepSchema = compatibleContractZodSchema(
  commonSchemaNames.sopStep,
);

/** Strict validator for stable A2A task/context coordinates used by resume requests. */
export const taskRefSchema = contractZodSchema("TaskRef");

/** Additive-field-compatible validator for inbound A2A task/context coordinates. */
export const compatibleTaskRefSchema = compatibleContractZodSchema("TaskRef");

type OptionalSchema<T> = [T] extends [never] ? null : z.ZodType<T>;
type SchemaFactory = <Name extends ContractSchemaName>(
  schemaName: Name,
) => z.ZodType<ContractSchemaDefinitions[Name]>;

/** Runtime schemas whose static types remain indexed by their owning skill. */
export type SkillRuntimeSchemas<K extends SkillId> = {
  input: z.ZodType<SkillInput<K>>;
  resume: OptionalSchema<SkillResume<K>>;
  signal: z.ZodType<SkillSignal<K>>;
  pause: OptionalSchema<SkillPause<K>>;
  output: z.ZodType<SkillOutput<K>>;
  artifact: z.ZodType<SkillArtifact<K>>;
};

function optionalContractSchema<T>(
  schemaFactory: SchemaFactory,
  schemaName: ContractSchemaName | null,
): OptionalSchema<T> {
  // The generated registry makes a null schema name equivalent to a `never`
  // contract slot. TypeScript cannot express that value/type correlation
  // through this generic helper, so these assertions restore only that link.
  if (schemaName === null) return null as OptionalSchema<T>;
  return schemaFactory(schemaName) as OptionalSchema<T>;
}

function buildSkillSchemas<K extends SkillId>(
  skillId: K,
  schemaFactory: SchemaFactory,
): SkillRuntimeSchemas<K> {
  const names = skillSchemaNames[skillId];
  return {
    input: schemaFactory(names.input),
    resume: optionalContractSchema<SkillResume<K>>(schemaFactory, names.resume),
    signal: schemaFactory(names.signal),
    pause: optionalContractSchema<SkillPause<K>>(schemaFactory, names.pause),
    output: schemaFactory(names.output),
    artifact: schemaFactory(names.artifact),
  };
}

function buildSchemaRegistry(schemaFactory: SchemaFactory): {
  [K in SkillId]: SkillRuntimeSchemas<K>;
} {
  const entries = skillIds.map((skillId) => [
    skillId,
    buildSkillSchemas(skillId, schemaFactory),
  ]);

  // Object.fromEntries cannot retain the correlation between each tuple's key
  // and its generic value. The source ids are the generated exhaustive tuple,
  // and buildSkillSchemas receives that same key, so this assertion is limited
  // to restoring that one lost mapped-type relationship.
  return Object.fromEntries(entries) as {
    [K in SkillId]: SkillRuntimeSchemas<K>;
  };
}

/**
 * Strict validators for every current skill. Unknown fields, unknown variants,
 * and malformed known variants are rejected according to the Pydantic source.
 */
export const skillSchemas = buildSchemaRegistry(contractZodSchema);

/**
 * Additive-field-compatible consumer schemas. Use these only at inbound wire
 * boundaries; producers and request constructors should use `skillSchemas`.
 */
export const compatibleSkillSchemas = buildSchemaRegistry(
  compatibleContractZodSchema,
);

/** Return the strict runtime contract for a selected skill. */
export function schemasForSkill<K extends SkillId>(
  skillId: K,
): SkillRuntimeSchemas<K> {
  return skillSchemas[skillId];
}

/** The caller-side spend-attribution identifiers carried on every request. */
export type SkillRequestAttribution<K extends SkillId> = Partial<
  Pick<
    SkillRequestMetadata<K>,
    "organizationId" | "userId" | "agentRunId" | "workflowRunId" | "stepRunId"
  >
>;

/** Build the routing metadata every typed request carries over A2A. */
export function skillRequestMetadata<K extends SkillId>(
  skillId: K,
  infrastructure?: Partial<
    Pick<
      SkillRequestMetadata<K>,
      "kernelSessionId" | "traceparent" | "tracestate"
    >
  >,
  attribution?: SkillRequestAttribution<K>,
): SkillRequestMetadata<K> {
  return {
    skillId,
    contractVersion: CONTRACT_VERSION,
    contractRevision,
    ...infrastructure,
    ...attribution,
  };
}

/**
 * Validate the browser-use envelope and then validate its dynamic `output`
 * property with the same caller-owned Zod schema used to request that output.
 */
export function parseBrowserUseOutput<TSchema extends z.ZodType>(
  value: unknown,
  outputSchema: TSchema,
): BrowserUseOutputForSchema<TSchema> {
  const envelope = skillSchemas["operator.browser_use"].output.parse(value);
  const result = outputSchema.parse(envelope.output);
  return { ...envelope, output: result };
}
